import React, { FunctionComponent, useContext } from "react";
import { CSSTransition } from "react-transition-group";

import { AnimationContext } from "./Animation";
import { TAnimationName } from "./types";

interface Props {
  name: TAnimationName;
  mountOnEnter?: boolean;
  timeout?: number;
  disabled?: boolean;
}

/**
 * Components wrapped with <AnimationTarget> will animate itselves responding to ancestor <Animation> component
 *
 * @param {TAnimationName} name name of played animation
 * @param {number} timeout duration of animation (best to match with CSS duration!)
 * @param {boolean} mountOnEnter target will be only mounted after first trigger of animation
 * (when <Animation> 'animate' prop goes true for the first time - useful for something appearing on hover)
 * @param {boolean} disabled determines whether animations are applied
 * @returns {FunctionComponent}
 */
export const AnimationTarget: FunctionComponent<Props> = ({
  name = "fade",
  timeout = 300,
  mountOnEnter,
  disabled,
  children,
}) => {
  const { animating, onFinish } = useContext(AnimationContext);

  if (!children) {
    return null;
  }

  return (
    <CSSTransition
      in={animating}
      timeout={timeout}
      classNames={name}
      appear={!disabled}
      enter={!disabled}
      exit={!disabled}
      mountOnEnter={mountOnEnter}
      onExited={onFinish}>
      {children}
    </CSSTransition>
  );
};
